import { useState, useEffect, useCallback } from 'react';
import { jobService } from '../services/jobService';

/**
 * Custom hook for managing jobs
 */
export const useJobs = (status = null, search = null) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await jobService.getAll(status, 0, 100, search);
      setJobs(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const createJob = async (jobData) => {
    setLoading(true);
    setError(null);
    try {
      const newJob = await jobService.create(jobData);
      setJobs(prev => [...prev, newJob]);
      return newJob;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create job');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateJob = async (jobId, jobData) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await jobService.update(jobId, jobData);
      setJobs(prev => prev.map(job => job.id === jobId ? updated : job));
      return updated;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update job');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteJob = async (jobId) => {
    setLoading(true);
    setError(null);
    try {
      await jobService.delete(jobId);
      setJobs(prev => prev.filter(job => job.id !== jobId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete job');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    jobs,
    loading,
    error,
    fetchJobs,
    createJob,
    updateJob,
    deleteJob
  };
};
